import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

const pendingRequests = new Map<string, Promise<any>>();

function serializeParams(params: any) {
  try {
    if (!params) return '';
    return JSON.stringify(params, Object.keys(params).sort());
  } catch (e) {
    return String(params);
  }
}

function isCORSError(error: any) {
  if (!error?.response && error?.message?.toLowerCase().includes('cors')) return true;
  if (error?.message?.toLowerCase().includes('network error') && !error?.response) return true;
  return false;
}

function isNetworkError(error: any) {
  // Exclude CORS errors - they should not be treated as network errors
  if (isCORSError(error)) return false;
  
  if (error?.code === 'ECONNABORTED' || error?.message?.toLowerCase().includes('cancelled')) return true;
  if (error?.response?.status === 502 || error?.response?.status === 503 || error?.response?.status === 504) return true;
  if (error?.code === 'ENOTFOUND' || error?.code === 'ECONNREFUSED' || error?.code === 'ETIMEDOUT') return true;
  return false;
}

function retryRequest(requestFn: () => Promise<any>, retries = 3): Promise<any> {
  let attempts = 0;
  const attempt = (): Promise<any> => {
    attempts++;
    return requestFn().catch((err) => {
      // Only retry on network errors
      if (isNetworkError(err) && attempts < retries) {
        return attempt();
      }
      // If it's a network error, mark it and add skipToast flag
      if (isNetworkError(err)) {
        return Promise.reject({ ...err, networkError: true, _skipToast: true });
      }
      // If it's a CORS error, mark it separately
      if (isCORSError(err)) {
        return Promise.reject({ ...err, corsError: true, _skipToast: true });
      }
      // Otherwise reject as normal (preserves original error behavior)
      return Promise.reject(err);
    });
  };
  return attempt();
}

const originalGet = api.get.bind(api);
const originalPost = api.post.bind(api);
const originalPut = api.put.bind(api);
const originalDelete = api.delete.bind(api);

api.get = function (url: string, config?: any) {
  const paramsKey = serializeParams(config?.params);
  const key = `GET::${url}::${paramsKey}`;
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key) as Promise<any>;
  }
  const promise = retryRequest(() => originalGet(url, config))
    .then((res: any) => {
      pendingRequests.delete(key);
      return res;
    })
    .catch((err: any) => {
      pendingRequests.delete(key);
      if (err.corsError) {
        if (window && window.location) {
          window.location.replace('/internal-server-error');
        }
      } else if (err.networkError) {
        if (window && window.location) {
          window.location.replace('/network-error');
        }
      }
      return Promise.reject(err);
    });
  pendingRequests.set(key, promise);
  return promise;
} as any;

api.post = function (url: string, data?: any, config?: any) {
  return retryRequest(() => originalPost(url, data, config))
    .catch((err: any) => {
      if (err.corsError) {
        if (window && window.location) {
          window.location.replace('/internal-server-error');
        }
      } else if (err.networkError) {
        if (window && window.location) {
          window.location.replace('/network-error');
        }
      }
      return Promise.reject(err);
    });
} as any;

api.put = function (url: string, data?: any, config?: any) {
  return retryRequest(() => originalPut(url, data, config))
    .catch((err: any) => {
      if (err.corsError) {
        if (window && window.location) {
          window.location.replace('/internal-server-error');
        }
      } else if (err.networkError) {
        if (window && window.location) {
          window.location.replace('/network-error');
        }
      }
      return Promise.reject(err);
    });
} as any;

api.delete = function (url: string, config?: any) {
  return retryRequest(() => originalDelete(url, config))
    .catch((err: any) => {
      if (err.corsError) {
        if (window && window.location) {
          window.location.replace('/internal-server-error');
        }
      } else if (err.networkError) {
        if (window && window.location) {
          window.location.replace('/network-error');
        }
      }
      return Promise.reject(err);
    });
} as any;

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "";

    const isUnauthorized401 =
      error?.response?.status === 401 &&
      message.toLowerCase().includes("unauthor");

    if (isUnauthorized401) {
      error._skipToast = true;
    }

    return Promise.reject(error);
  }
);

export default api; 